/**
 * Apply aged care downsize strategy options to factFind (manual control + suggestion engine).
 * Ported verbatim from frontend/src/calculations/agedCareDownsizeFactFind.js.
 */

import { applyDownsizerContributionsToAssets } from './downsizerContributionUtils.ts';
import type { FactFind } from './factFindTypes.ts';
import type { StrategyOptions } from './strategyOptionsTypes.ts';

/** Apply downsizeHome strategyOptions to a cloned factFind. */
export function applyAgedCareDownsizeToFactFind(factFind: FactFind, strategyOptions: StrategyOptions = {}): any {
    if (!strategyOptions.downsizeHome || !strategyOptions.downsizeDetails) {
        return factFind;
    }

    const downsizeDetails = strategyOptions.downsizeDetails;
    const modified = structuredClone(factFind);
    if (!modified.assets) modified.assets = {};
    if (!modified.home) modified.home = {};

    const originalHomeValue = factFind.home?.value || 0;
    const sellingCosts = strategyOptions.sellingCosts || 0;
    // The duty on the new home, recorded with the downsize details it belongs to (23 Sep 2026).
    const stampDuty = Number(downsizeDetails?.stampDuty) || 0;
    const netProceeds = Math.max(0, originalHomeValue - sellingCosts - stampDuty);

    modified.home.value = (downsizeDetails.newHomePurchasePrice || 0) > 0 ? downsizeDetails.newHomePurchasePrice : 0;

    const existingRadPaid = (modified.assets.radPaid || []).reduce((sum: number, asset: any) => sum + (asset.value || 0), 0);
    const totalRadPaid = existingRadPaid + (downsizeDetails.radAllocation || 0);

    if (totalRadPaid > 0) {
        modified.assets.radPaid = [
            {
                id: 'rad-paid',
                description: 'RAD Paid',
                value: totalRadPaid,
                ownership: 'client',
            },
        ];
    }

    applyDownsizerContributionsToAssets(modified.assets, factFind, downsizeDetails, {
        idPrefix: 'downsizer-contrib',
        partnerIdPrefix: 'partner-downsizer-contrib',
    });

    const totalAllocated =
        (downsizeDetails.radAllocation || 0) +
        (downsizeDetails.newHomePurchasePrice || 0) +
        (downsizeDetails.downsizerContribution || 0) +
        (downsizeDetails.partnerDownsizerContribution || 0);

    const unallocatedProceeds = netProceeds - totalAllocated;

    if (unallocatedProceeds > 0) {
        if (!modified.assets.bankAccounts) modified.assets.bankAccounts = [];
        if (modified.assets.bankAccounts.length > 0) {
            const first = modified.assets.bankAccounts[0]!;
            first.value = (first.value || 0) + unallocatedProceeds;
        } else {
            modified.assets.bankAccounts.push({
                id: 'downsize-proceeds',
                type: 'bankAccounts',
                description: 'Downsize Proceeds',
                value: unallocatedProceeds,
                ownership: 'client',
                incomeRate: 2,
                growthRate: 0,
            });
        }
    }

    return modified;
}
